import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as PropTypes from 'prop-types';
import get from 'lodash/get';
import { injectIntl } from 'react-intl';

import {
  makeAppointmentProviderByIdSelect,
  selectMyProvidersVisitTypes,
  selectProgressOrganizationId,
  selectProgressReason,
  selectProgressRescheduleData,
  selectProgressVisitType,
  selectScheduleMetaDataFromProviderInfo,
  selectSchedulePayloadData,
} from '../../../redux/appointmentForm/selectors';
import * as appointmentProgressConsents from '../../../redux/appointmentForm/requests/consent-req';
import * as providerGWDetails from '../../../redux/appointmentForm/requests/provider-GW-details-req';
import * as providerDetails from '../../../redux/appointmentForm/requests/provider-details-req';
import { appointmentProgressActions } from '../../../redux/appointmentForm/progress-types';
import { selectLanguageTypes } from '../../../redux/data/selectors';
import { redirectToProviders } from '../../../utils/appointments/redirect-utils';
import AppointmentHeader from '../Components/AppointmentHeader';
import ProviderCalendar from './ProviderCalendar';
import ProviderInfo from './ProviderInfo';
import LeaveFlowModal from '../Components/LeaveFlowModal';
import {
  OFFICE_VISIT_TYPE_CODE,
  PROVIDER_CALENDAR_SOURCE,
} from '../../../constants';
import { usePageView } from '../../../hocs/usePageView';
import MetaHelmet from '../../AppHelmet/MetaHelmet';
import ProviderVisitTypeToggle from './ProviderVisitTypeToggle';

function AppointmentProviderDetails({
  formatMessage,
  params: { id },
  location: { hash },
}) {
  const confirmModalRef = useRef();
  const dispatch = useDispatch();

  const [currentDate, setCurrentDate] = useState(new Date().toISOString());

  const locationId = hash.slice(1, hash.length);
  const rescheduleData = useSelector(selectProgressRescheduleData);
  const reason = useSelector(selectProgressReason);
  const scheduleMeta = useSelector(selectScheduleMetaDataFromProviderInfo);
  const provider = useSelector(
    makeAppointmentProviderByIdSelect(id, locationId),
  );
  const languageTypes = useSelector(selectLanguageTypes);
  const payload = useSelector(selectSchedulePayloadData);
  const organizationId = useSelector(selectProgressOrganizationId);
  const visitType = useSelector(selectProgressVisitType);
  const visitTypesList = useSelector(selectMyProvidersVisitTypes);

  const isRescheduleFlow = !!get(rescheduleData, 'id');
  const rescheduleProvider = get(rescheduleData, 'providerInfo', {});
  const isOfficeVisit = visitType.code === OFFICE_VISIT_TYPE_CODE;

  const safeProvider = isRescheduleFlow ? rescheduleProvider : provider;

  const clearProgressConsents = () => {
    dispatch(appointmentProgressConsents.actions.clear());
  };

  usePageView('Get Care | Provider Details');

  useEffect(
    () => () => {
      dispatch(providerDetails.actions.clear());
      dispatch(providerGWDetails.actions.clear());
    },
    [],
  );

  const fetchProviderDetails = useCallback(
    (date, visitTypeId) => {
      const data = { ...payload, providerId: id, date };

      setCurrentDate(date);

      dispatch(
        providerDetails.actions.send({
          reasonTypeId: data.payload.reasonTypeId,
          serviceId: data.payload.serviceId,
          organizationId,
          visitTypeId: visitTypeId || visitType.id,
          date: date || currentDate,
          userId: data.userId,
          providerProfileId: id,
          ...(!isRescheduleFlow && { priorityLocationId: locationId }),
        }),
      );
    },
    [
      id,
      payload,
      visitType,
      locationId,
      currentDate,
      organizationId,
      isRescheduleFlow,
    ],
  );

  const setVisitType = data => {
    dispatch(appointmentProgressActions.setVisitType(data));
    fetchProviderDetails(currentDate, data.id);
  };

  const handleTimeSelect = (time, location, timeSlotsSource, date) => {
    const data = {
      ...scheduleMeta,
      provider: safeProvider,
      time,
      location,
      timeSlotsSource,
    };

    dispatch(appointmentProgressActions.setScheduleData(data));

    if (timeSlotsSource !== PROVIDER_CALENDAR_SOURCE) {
      dispatch(
        providerGWDetails.actions.send({
          ...payload,
          providerId: id,
          date,
          organizationId,
          timeSlotsSource,
        }),
      );
    }

    dispatch(
      appointmentProgressActions.setCreateTypeFlowGW(
        timeSlotsSource !== PROVIDER_CALENDAR_SOURCE,
      ),
    );

    dispatch(
      appointmentProgressActions.determineNextStepAfterAppointmentProviders(),
    );
  };

  const handleBack = () => {
    if (isRescheduleFlow) {
      confirmModalRef.current.open();
    } else {
      redirectToProviders();
    }
  };

  return (
    <Fragment>
      <MetaHelmet title="pages.title.getCare.providerInfo" />
      <AppointmentHeader handleBack={handleBack} />
      <ProviderInfo provider={safeProvider} languageTypes={languageTypes} />
      {isRescheduleFlow && (
        <ProviderVisitTypeToggle
          isOfficeVisit={isOfficeVisit}
          reasonType={reason.reasonType}
          manageVisitType={{ setVisitType, visitType, visitTypesList }}
        />
      )}
      <ProviderCalendar
        onDateChange={fetchProviderDetails}
        onTimeSelect={handleTimeSelect}
        isOfficeVisit={isOfficeVisit}
      />
      <LeaveFlowModal
        ref={confirmModalRef}
        formatMessage={formatMessage}
        clearProgressConsents={clearProgressConsents}
      />
    </Fragment>
  );
}

AppointmentProviderDetails.propTypes = {
  params: PropTypes.object,
  location: PropTypes.object,
  formatMessage: PropTypes.func,
};

export default injectIntl(AppointmentProviderDetails);



// WEBPACK FOOTER //
// ./src/components/AppointmentForm/AppointmentProviderDetails/index.jsx